import React, { useState } from 'react';
import { Form, Table, Button, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useCreateProductMutation } from '../../slices/productsApiSlice';
import { useSelector } from 'react-redux';

const CreateMultipleProductScreen = () => {
  const [products, setProducts] = useState([]);
  const [fileName, setFileName] = useState('');
  const { userInfo } = useSelector((state) => state.auth);
  const [createProduct, { isLoading }] = useCreateProductMutation();

  const validateProduct = (p) => {
    if (!p.name?.trim()) return 'Missing name';
    if (!p.brand?.trim()) return 'Missing brand';
    if (!p.category?.trim()) return 'Missing category';
    if (!Array.isArray(p.images) || p.images.length === 0) return 'Missing images';
    if (isNaN(parseFloat(p.price))) return 'Invalid price';
    if (isNaN(parseInt(p.countInStock))) return 'Invalid countInStock';
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = rows.map((row, index) => {
        const product = {
          rowIndex: index + 2,
          name: row.Name?.trim() || '',
          brand: row.Brand?.trim() || '',
          category: row.Category?.trim() || '',
          description: row.Description?.trim() || '',
          price: parseFloat(row.Price),
          countInStock: parseInt(row.CountInStock),
          images: [],
          error: null,
          status: null,
          message: null,
          loading: false,
        };
        product.error = validateProduct(product);
        return product;
      });

      setProducts(enriched);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleImageUpload = (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const updated = [...products];
      updated[idx].images = [reader.result];
      updated[idx].error = validateProduct(updated[idx]);
      setProducts(updated);
    };
    reader.readAsDataURL(file);
  };

  const handleFieldChange = (idx, field, value) => {
    const updated = [...products];
    updated[idx][field] = field === 'price' || field === 'countInStock' ? Number(value) : value;
    updated[idx].error = validateProduct(updated[idx]);
    setProducts(updated);
  };

  const handleSubmitAll = async () => {
    const updated = [...products];

    for (let i = 0; i < updated.length; i++) {
      const p = updated[i];
      updated[i].loading = true;
      updated[i].status = 'Submitting';
      setProducts([...updated]);

      const error = validateProduct(p);
      if (error) {
        updated[i].status = 'Failed';
        updated[i].message = error;
        updated[i].loading = false;
        setProducts([...updated]);
        continue;
      }

      try {
        await createProduct({
          name: p.name,
          price: p.price,
          brand: p.brand,
          category: p.category,
          description: p.description,
          images: p.images,
          countInStock: p.countInStock,
        }).unwrap();

        updated[i].status = 'Success';
        updated[i].message = 'Created';
      } catch (err) {
        updated[i].status = 'Failed';
        updated[i].message = err?.data?.message || 'Creation error';
      } finally {
        updated[i].loading = false;
        setProducts([...updated]);
      }
    }

    toast.success('All rows processed');
  };

  return (
    <div className="container mt-4">
      <h2>📦 Bulk Product Creation with Inline Editing</h2>

      <Form.Group controlId="excelUpload" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx" onChange={handleFileUpload} />
      </Form.Group>

      {products.length > 0 && (
        <>
          <h5>Preview & Edit: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Row</th>
                <th>Name</th>
                <th>Brand</th>
                <th>Category</th>
                <th>Price</th>
                <th>CountInStock</th>
                <th>Image</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, idx) => (
                <tr key={idx} style={{ background: p.error ? '#fff3cd' : 'inherit' }}>
                  <td>{p.rowIndex}</td>
                  <td>
                    <Form.Control
                      value={p.name}
                      onChange={(e) => handleFieldChange(idx, 'name', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      value={p.brand}
                      onChange={(e) => handleFieldChange(idx, 'brand', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      value={p.category}
                      onChange={(e) => handleFieldChange(idx, 'category', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={p.price}
                      onChange={(e) => handleFieldChange(idx, 'price', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={p.countInStock}
                      onChange={(e) => handleFieldChange(idx, 'countInStock', e.target.value)}
                    />
                  </td>
                  <td>
                    {p.images.length > 0 ? (
                      p.images.map((img, i) => (
                        <img key={i} src={img} alt="preview" style={{ width: '20px', height: '20px', marginRight: '5px' }} />
                      ))
                    ) : (
                      <Form.Control type="file" accept="image/*" onChange={(e) => handleImageUpload(e, idx)} />
                    )}
                  </td>
                  <td>
                    {p.loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : p.status === 'Success' ? (
                      <span className="text-success">✅ {p.message}</span>
                    ) : p.status === 'Failed' ? (
                      <span className="text-danger">❌ {p.message}</span>
                    ) : p.error ? (
                      <span className="text-warning">{p.error}</span>
                    ) : (
                      <span className="text-muted">Ready</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button onClick={handleSubmitAll} disabled={isLoading}>
            {isLoading ? <Spinner animation="border" size="sm" /> : 'Submit All'}
          </Button>
        </>
      )}
    </div>
  );
};

export default CreateMultipleProductScreen;